/**
 * Routes PDP add-to-cart between Shopify and UnitMOQ based on variant type and customization.
 */
const unitMoqAtc = {
  originalConfiguration: null,
  productHasChanged: false,
  listenersAttached: false,
  skipToggleReset: false,

  reset() {
    this.originalConfiguration = null;
    this.productHasChanged = false;
  },

  getConfigFingerprint(detail) {
    if (!detail) return null;
    return (detail.options || []).map((opt) => ({
      id: opt.id ?? opt.optionId ?? opt.type ?? opt.name,
      value: opt.value ?? opt.selected ?? opt.choice ?? opt.label,
    }));
  },

  setOriginalConfiguration(detail) {
    this.originalConfiguration = this.getConfigFingerprint(detail);
    this.productHasChanged = false;
  },

  configurationsMatch(detail) {
    if (!this.originalConfiguration) return false;
    return JSON.stringify(this.originalConfiguration) === JSON.stringify(this.getConfigFingerprint(detail));
  },

  isInStockMode() {
    if (document.querySelector('.in-stock-toggle__input:checked')) return true;
    return this.isReadyToShipVariant();
  },

  exitInStockModeForCustomization() {
    this.skipToggleReset = true;

    document.querySelectorAll('.in-stock-toggle__input').forEach((input) => {
      input.checked = false;
    });
    document.querySelector('.product')?.setAttribute('data-type', 'custom');
    window.uncheckVariantSwatches?.();
    const url = new URL(window.location.href);
    url.searchParams.delete('variant');
    window.history.replaceState({}, '', url.toString());
    this.skipToggleReset = false;
  },

  handleLoaded(event) {
    this.setOriginalConfiguration(event?.detail);
  },

  handleUpdated(event) {
    const detail = event?.detail;
    if (!detail) return;

    if (!this.originalConfiguration) {
      this.setOriginalConfiguration(detail);
      return;
    }

    if (this.configurationsMatch(detail)) return;

    if (!this.productHasChanged && this.isInStockMode()) {
      this.exitInStockModeForCustomization();
    }
    this.productHasChanged = true;
  },

  getRtsVariantIds() {
    return (
      document.querySelector('variant-selects[data-rts-variants]')?.dataset?.rtsVariants?.split(',').filter(Boolean) ||
      []
    );
  },

  isReadyToShipVariant() {
    const productEl = document.querySelector('.product');
    if (productEl?.dataset?.template === 'sale') return true;

    const variantId = document.querySelector('[name="variant_id"]')?.value;
    if (!variantId) return false;

    if (this.getRtsVariantIds().includes(String(variantId))) return true;

    const selectedOption = document
      .querySelector('.color-options-container .option-item input:checked')
      ?.closest('.option-item');
    return Boolean(selectedOption?.classList.contains('is-rts'));
  },

  hasActiveDesign() {
    if (new URLSearchParams(window.location.search).get('design')) return true;
    if (document.querySelector('.product')?.hasAttribute('data-has-active-design')) return true;
    if (document.querySelector('.design-options-container .option-item.is-design input:checked')) return true;
    if (document.querySelector('.option-item.is-saved input:checked')) return true;
    return false;
  },

  shouldUseStandardShopifyAtc() {
    console.log('shouldUseStandardShopifyAtc', this.hasActiveDesign(), this.isReadyToShipVariant(), !this.productHasChanged);
    if (this.hasActiveDesign()) return false;
    if (!this.isReadyToShipVariant()) return false;
    return !this.productHasChanged;
  },
};

window.unitMoqAtc = unitMoqAtc;

const attachUnitMoqAtcListeners = () => {
  if (!window.UnitMOQ || unitMoqAtc.listenersAttached) return unitMoqAtc.listenersAttached;

  if (window.UnitMOQ.EVENT_LOADED) {
    window.UnitMOQ.on(window.UnitMOQ.EVENT_LOADED, (event) => unitMoqAtc.handleLoaded(event));
  }
  if (window.UnitMOQ.EVENT_DESIGN_LOADED) {
    window.UnitMOQ.on(window.UnitMOQ.EVENT_DESIGN_LOADED, (event) => unitMoqAtc.handleLoaded(event));
  }
  if (window.UnitMOQ.EVENT_UPDATED) {
    window.UnitMOQ.on(window.UnitMOQ.EVENT_UPDATED, (event) => unitMoqAtc.handleUpdated(event));
  }
  if (window.UnitMOQ?.beta?.session?.EVENT_UPDATED) {
    window.UnitMOQ.beta.session.on(window.UnitMOQ.beta.session.EVENT_UPDATED, (event) =>
      unitMoqAtc.handleUpdated(event)
    );
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, () => unitMoqAtc.reset());
  }

  document.querySelectorAll('.in-stock-toggle__input').forEach((input) => {
    input.addEventListener('change', () => {
      if (unitMoqAtc.skipToggleReset) return;
      unitMoqAtc.reset();
    });
  });

  unitMoqAtc.listenersAttached = true;
  return true;
};

const initUnitMoqAtcListeners = () => {
  if (attachUnitMoqAtcListeners()) return;

  let attempts = 0;
  const waitForUnitMoq = setInterval(() => {
    attempts += 1;
    if (attachUnitMoqAtcListeners() || attempts >= 100) {
      clearInterval(waitForUnitMoq);
    }
  }, 100);
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initUnitMoqAtcListeners);
} else {
  initUnitMoqAtcListeners();
}

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.form.querySelector('[name=id]').disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        if (this.submitButton && document.querySelector('cart-drawer')) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      /**
       * Main PDP buy buttons use `class="product-form"` on this element and UnitMOQ.
       * Collection quick-add uses `<product-form>` without that class — use Shopify fetch instead.
       */
      shouldUseUnitMOQ() {
        if (!this.classList.contains('product-form') || typeof window.UnitMOQ === 'undefined') {
          return false;
        }
        return !window.unitMoqAtc?.shouldUseStandardShopifyAtc?.();
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.shouldUseUnitMOQ()) {
          this.onSubmitUnitMOQ(evt);
        } else {
          console.log('onSubmitStandard');
          this.onSubmitStandard(evt);
        }
      }

      onSubmitUnitMOQ() {
        const $btn = $('.product-form__buttons #umoq-add-to-cart-btn');
        const $allBtns = $('.product-form__buttons button');
        const $disable = $('unitmoq-product-details, product-form');
        const original = $btn.text();
        $allBtns.prop('aria-disabled', true);
        $disable.addClass('loading');
        $btn.text('Adding…');
        const prodObj = $('.product')?.data('obj');
        const variantId = $('[name="variant_id"]')?.val();
        const rtsVariantIds =
          document.querySelector('variant-selects[data-rts-variants]')?.dataset?.rtsVariants?.split(',').filter(Boolean) ||
          [];
        const isRTS = rtsVariantIds.includes(String(variantId));
        const revert = () => {
          $allBtns.prop('aria-disabled', false);
          $disable.removeClass('loading');
          $btn.text(original);
        };
        const propertiesEvent = $('[name="properties[Event]"]')?.val();
        const properties = {
          ...(propertiesEvent ? { Event: propertiesEvent } : {}),
          _productId: prodObj?.productId,
          _designName: `${prodObj?.productTitle}${isRTS ? ' (In Stock)' : ''}`,
          _addToCart: `${window.location.origin}/products/${prodObj?.productHandle}?variant=${variantId}`,
        };
        console.log('onSubmitUnitMOQ', properties);
        window.UnitMOQ.addToCart(1, properties)
          .then(async () => {
            const sections = ['cart-drawer', 'cart-icon-bubble'];
            const res = await fetch(`${routes.cart_change_url}.js`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({
                line: 1,
                quantity: 1,
                sections,
                sections_url: window.location.pathname,
              }),
            });
            const data = await res.json();
            this.cart.renderContents(data);
            revert();
          })
          .catch((e) => {
            console.error(e);
            revert();
          });
      }

      onSubmitStandard(evt) {
        if (!this.submitButton || this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.error = false;
        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        let atcTextEl = this.submitButton.querySelector('.atc-text');
        if (atcTextEl) {
          atcTextEl.textContent = 'Adding to cart...';
          atcTextEl.classList.add('animated');
          atcTextEl.classList.add('fadeInDown');
        }
        const spinner = this.submitButton.querySelector('.loading__spinner');
        if (spinner) spinner.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        const variantIdInput = this.form.querySelector('[name="variant_id"]');
        if (variantIdInput?.value) {
          formData.set('id', variantIdInput.value);
        }
        if (this.cart) {
          formData.append('sections', this.cart.getSectionsToRender().map((section) => section.id));
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;
        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            this.submitButton.classList.remove('disabled');
            this.submitButton.classList.add('animated');
            this.submitButton.classList.add('zoomOut');
            const spin = this.submitButton.querySelector('.loading__spinner');
            if (spin) spin.classList.add('hidden');
            atcTextEl = this.submitButton.querySelector('.atc-text');
            if (atcTextEl) {
              atcTextEl.classList.remove('animated');
              atcTextEl.classList.remove('fadeInDown');
            }
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) {
              this.submitButton.removeAttribute('aria-disabled');
              if (atcTextEl) {
                atcTextEl.textContent = `Added to cart`;
              }
            }
          });
      }
    }
  );
}
